import urlJoin from 'url-join';
import type { Whitelabel } from '@openticket/lib-whitelabels';
import { rudderAnalyticsTrack } from './rudderstack';

export const SIGN_IN_ACTION = {
    AUTO_REDIRECT: 'auto-redirect' as const,
    HIDE: 'hide' as const,
    SHOW: 'show' as const,
} as const;

export const REFERRER_ACTION = {
    LOGOUT: 'logout' as const,
} as const;

export type SignInAction = typeof SIGN_IN_ACTION[keyof typeof SIGN_IN_ACTION];
export type SignInActionOverride = SignInAction | `no-${SignInAction}`;

export interface SignInProvider {
    provider: string;
    action?: typeof SIGN_IN_ACTION.AUTO_REDIRECT | typeof SIGN_IN_ACTION.HIDE | typeof SIGN_IN_ACTION.SHOW;
}

export interface SignInActionOverrides {
    [provider: string]: SignInActionOverride;
}

export function resolveSignInAction(whitelabel: Whitelabel): SignInProvider | null {
    if (!whitelabel.login.sso) {
        return null;
    }

    // Multiple actions can adjust the way single-sign-on is handled.
    // When returning from a sso-provider, the auth service adds a sso_provider query parameter to recognize this.
    // When a down-stream application logs-out and redirects back to the login app, any automatic sso-redirection will result
    // in a new successful login. Effectively negating the logout action. The referrer_action can be set to 'logout'
    // to prevent this. (DD-4E8A)
    // Query parameters can be used to override the configuration values for credential and sso login. This is most useful for
    // showing the credentials input, when normally auto-redirect would immediately redirect to the sso-provider.

    const params = new URLSearchParams(window.location.search);
    const ssoProviderReturn: string | null = params.get('sso_provider');
    const referrerAction: string | null = params.get('referrer_action');
    const ssoOverrides = getSSOActionOverrides(); // query: login_sso[{provider}]={action}

    const providers = whitelabel.login.sso satisfies Record<string, { action: string }>;

    // DD-4E8B - Sign in overrides can only be applied on sso providers which are configured for a whitelabel.
    // If an override is wanted for a default action, the provider must be explicitly configured first.
    for (const [ provider, { action } ] of Object.entries(providers)) {
        const resolvedAction = combineSignInActions(action, ssoOverrides[`login_sso[${provider}]`]);

        if (resolvedAction === SIGN_IN_ACTION.AUTO_REDIRECT) {
            if (ssoProviderReturn === provider) {
                // DD-4E88 - The redirect has already happened, this will prevent an automatic redirect loop.
                return null;
            }

            if (referrerAction === REFERRER_ACTION.LOGOUT) {
                // DD-4E8A - While auto-redirect after an explicit user logout is theoretically problematic, the current SSO
                // provider has a session duration of 10 minutes. Until that changes or more SSO providers are added,
                // this (preventative) feature does not need to be implemented.
                //
                // The referrer action is not linked to a specific SSO provider,
                // therefore, all auto-redirects should be suppressed,
                // but an early return would make future maintenance on this code harder.
                continue;
            }

            return { provider, action: SIGN_IN_ACTION.AUTO_REDIRECT };
        }
    }

    return null;
}

export function redirectToSSOProvider(whitelabel: Whitelabel, { provider, action }: SignInProvider): void {
    const inParams = new URLSearchParams(window.location.search);

    const clientId: string | null = inParams.get('client_id');
    const state: string | null = inParams.get('state');
    const redirectUri: string | null = inParams.get('redirect_uri') || inParams.get('redirect_url');

    if (!clientId || !state) {
        throw new Error('Missing required parameters client_id and state in signInAction redirect URL query params.');
    }

    const outParams: { state: string; client_id: string; redirect_uri?: string } = {
        client_id: clientId,
        state,
    };

    if (redirectUri) {
        outParams.redirect_uri = redirectUri;
    }

    const url = urlJoin(
        whitelabel.login.auth_api_url,
        'sessions',
        'sso',
        provider,
        'login',
        `?${new URLSearchParams(outParams).toString()}`,
    );

    try {
        rudderAnalyticsTrack('login sso', { provider, action: action ?? 'manual' });
    } catch (e) {
        // TODO log
    }

    window.location.href = url;
}

export function isSignInAction(value: string): value is SignInAction {
    return (Object.values(SIGN_IN_ACTION) as string[]).includes(value);
}

export function isSignInActionOverride(value: string): value is SignInActionOverride {
    const inverse = Object.values(SIGN_IN_ACTION).map((action: SignInAction) => `no-${action}`);
    return isSignInAction(value) || inverse.includes(value);
}

export function getSSOActionOverrides(): SignInActionOverrides {
    return Object.fromEntries(Array.from(new URLSearchParams(window.location.search).entries())
        .filter((pair): pair is [string, SignInActionOverride] => /^login_sso\[.*]$/.test(pair[0]) && isSignInActionOverride(pair[1])));
}

export function getCredentialsActionOverride(): SignInActionOverride | null {
    const override = new URLSearchParams(window.location.search).get('login_credentials');
    return override && isSignInActionOverride(override) ? override : null;
}

export function getSignInActionOverridesQueryString(): string {
    const overrides = Array.from(new URLSearchParams(window.location.search).entries())
        .filter((pair): pair is [string, SignInActionOverride] => /^login_(?:sso\[.*]|credentials)$/.test(pair[0]) && isSignInActionOverride(pair[1]))
        .map(([ key, value ]) => `${key}=${value}`)
        .join('&');

    return overrides ? `?${overrides}` : '';
}

export function combineSignInActions(action: string, override?: SignInActionOverride | null): SignInAction | null {
    if (override && isSignInAction(override)) {
        // When the override is a 'normal' SSO action, return it.
        return override;
    }

    if (override && isSignInActionOverride(override) && action === override.slice(3)) {
        // Only when the override negates the current action (no-...), return 'null'.
        // The default action will be performed.
        // Overrides cannot negate the default action.
        return null;
    }

    return isSignInAction(action) ? action : null;
}
